// src/utils/ProtectedRoute.js
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { hasApprovedMarketplaceAccess } from "./marketplaceAccess";

/**
 * Guards any route that requires authentication.
 *
 * tenant → always allowed (no payment enforcement here)
 * seller → enforces plan selection + payment before allowing access
 */
export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  const hasApprovedAccess = hasApprovedMarketplaceAccess(user);
  const isPaymentCallbackRoute = location.pathname.startsWith("/payment/callback");
  const isPricingRoute = location.pathname === "/dashboard/pricing";
  const isManagePlanRoute = location.pathname === "/dashboard/manage-plan";
  const hasTenantAccess =
    user?.hasTenantAccess === true || Boolean(user?.tenantId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 dark:border-gray-800 border-t-violet-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) return <Navigate to="/login" replace />;

  if (isPaymentCallbackRoute) {
    return children;
  }

  if (hasApprovedAccess) {
    return children;
  }

  if (isPricingRoute || isManagePlanRoute) {
    return children;
  }

  if (hasTenantAccess) {
    return <Navigate to="/tenant" replace />;
  }

  return <Navigate to="/dashboard/pricing" replace />;
}